import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  inject,
  signal
} from '@angular/core';
import { Observable } from 'rxjs';
import { IntakeFormType } from '@ee/common/enums';
import { CaseSettings, Eviction, IntakeEviction, IntakeForm, Organization, User, Workflow } from '@ee/common/models';
import { IntakeFormService } from '@ee/common/services';
import { isIntakeLayoutV2Enabled } from '@ee/common/utils';
import { isLegacyForm } from '@ee/common/intake-layout';

/**
 * Which wizard this firm's clients get.
 *
 * The same two facts that decide the *builder*, deliberately — one gate for the whole effort, so a firm
 * cannot end up authoring in v2 and serving v1:
 *
 *  - **Eligibility** is the `INTAKE_LAYOUT_V2` premium feature. Off by default, and off means nothing
 *    changes for anybody.
 *  - **Capability** is the form's own `layout_version`, which only the staff migration console moves.
 *    No flag substitutes for it: rendering a v2 wizard from a v1 form would ask questions from rows
 *    that do not exist yet.
 *
 * So: flag off, or form still v1 → the wizard they have. Both → the new one. A firm can be moved back
 * by turning the flag off, which is the property that makes a firm-by-firm rollout safe.
 *
 * Anonymous callers have no organisation to read a flag from, so the *form* answers for them: a public
 * link belongs to one firm, and its form carries the version. That is also why eligibility is resolved
 * from the passed-in org rather than the store — the public app has no logged-in anybody.
 */
@Component({
  selector: 'ee-eviction-intake-shell',
  // Declared in `EvictionIntakeModule`, not standalone: it renders v1's own components, and those are
  // module-declared. A standalone component cannot import them directly.
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (resolved()) {
      @if (useV2()) {
        <ee-eviction-wizard-v2 [attorneyId]="attorneyId" [forceIntakeForm]="forceIntakeForm"
                               [skipClientStep]="skipClientStep" [submitHandler]="submitHandler"
                               [loggedInOrg]="loggedInOrg" [loggedInUser]="loggedInUser"
                               [caseSettings]="caseSettings" [selectedClientId]="selectedClientId"
                               [cloneCase]="cloneCase" [workflows]="workflows" (addClient)="addClient.emit($event)"
                               (evictionCreated)="evictionCreated.emit($event)"></ee-eviction-wizard-v2>
      } @else {
        <!--
          v1, unchanged and still the default. Handed in by the host rather than instantiated here: it
          carries fifteen inputs, and duplicating that list is how the two paths drift.

          As an <ng-template>, not projected content: a projected component is created by the host
          whether or not this branch renders, so on the v2 path a hidden v1 wizard would still run its
          subscriptions and initialise the root IntakeEvictionFormService the v2 wizard is using.
        -->
        <ng-container [ngTemplateOutlet]="v1Template ?? null"></ng-container>
      }
    }
  `,
  // A definite width, so the hosts' centring cannot shrink-wrap the wizard. The portal page centres its
  // column (`items-center`) and the public app's shell is `md:flex-none`: both size a child to its
  // content. v1 brings its own fixed 1024px wrapper; v2 only caps itself (`max-width: 820px`), so
  // without this each step was as wide as whatever happened to be on it — Case setup, which is v1's
  // workflow cards, came out ~520px while the authored pages filled the cap.
  styles: [':host { display: block; width: 100%; }']
})
export class EvictionIntakeShellComponent implements OnInit {
  @Input() attorneyId?: string;

  @Input() forceIntakeForm = false;

  @Input() skipClientStep = false;

  @Input() submitHandler?: (intake: IntakeEviction) => Observable<string | undefined>;

  /** On the public link, the firm whose link it is: nobody is signed in, but its flag still applies. */
  @Input() loggedInOrg?: Organization;

  @Input() loggedInUser?: User;

  /** The firm's own step needs these; a client filling their own form does not have one. */
  @Input() caseSettings?: CaseSettings;

  @Input() selectedClientId?: string;

  @Input() cloneCase?: Eviction;

  /** The firm's workflows for the v2 firm step (v1 gets them through its own template). */
  @Input() workflows: Workflow[] | null = null;

  @Output() evictionCreated = new EventEmitter<string | undefined>();

  @Output() addClient = new EventEmitter<boolean>();

  /** The host's v1 wizard, wrapped in an `<ng-template>` so it is only created when v1 is chosen. */
  @ContentChild(TemplateRef) protected v1Template?: TemplateRef<unknown>;

  private readonly intakeFormService = inject(IntakeFormService);

  protected readonly resolved = signal(false);
  protected readonly useV2 = signal(false);

  ngOnInit(): void {
    // ISS-151: `GET /api/intake-form/type/{type}` is gated on ADMIN_INTAKE_FORM_VIEW (the form *editor*
    // permission), so reading it for the attorney's own New Eviction page 403s every staff user without
    // it and the interceptor tells them they lack permission for the thing they are doing. The org-scoped
    // endpoint is permitAll for exactly this reason; a logged-in org reads its own form through it, the
    // same way a client or PM reads the attorney's. Only a caller with no org at all falls back.
    const orgId = this.attorneyId ?? this.loggedInOrg?.id;
    const load = orgId
      ? this.intakeFormService.findByTypeAndOrganizationId(IntakeFormType.EVICTION, orgId)
      : this.intakeFormService.findByType(IntakeFormType.EVICTION);

    load.subscribe({
      next: form => this.decide(form),
      /*
       * If the form cannot be read, serve the wizard they already have.
       *
       * Guessing towards v2 would render a form from rows that may not exist. This is a client trying
       * to file an eviction; the failure has to land on the path that has worked for years.
       */
      error: () => {
        this.useV2.set(false);
        this.resolved.set(true);
      }
    });
  }

  private decide(form: IntakeForm): void {
    // Anonymous callers have no organisation to read a flag from, so the form answers for them: a
    // public link belongs to one firm, and a migrated form is only ever produced for an eligible one.
    const eligible = this.loggedInOrg ? isIntakeLayoutV2Enabled(this.loggedInOrg, this.loggedInUser) : true;

    this.useV2.set(eligible && !isLegacyForm(form));
    this.resolved.set(true);
  }
}
